import { Component, EventEmitter, Input, Output } from '@angular/core';
import { UiFilterModifiers } from './model/ui-filter.modifier';
import {
  TDurationGroupInfo,
  TFilterGroupModel,
  TOrgFilter,
  TRenderOptionType,
  TTargetDateGroupInfo,
} from './model/ui-filter.types';
import { ModalController, PopoverController } from '@ionic/angular';
import { OrgHeirarchyComponent } from '../org-heirarchy/org-heirarchy.component';
import { TOrgHeirachy } from '@monorepo/types';
import { DurationPickerComponent } from '../duration-picker/duration-picker.component';
import moment from 'moment';

@Component({
  selector: 'ui-filter',
  templateUrl: './filter.component.html',
  styleUrls: ['./filter.component.scss'],
})
export class FilterComponent<T> {
  latestFilterGoupModel!: TFilterGroupModel<T>;
  @Input() set fiterGroupModel(filter: TFilterGroupModel<T> | undefined) {
    if (filter) {
      this.latestFilterGoupModel = filter;
    }
  }

  @Input() scrollableX = false;

  /**
   * renderOptionType - render Option based on type
   * - INLINE: Options window will open just below the options
   * - POPUP: Options popup will show in the center of the screen
   */
  @Input() renderOptionType: TRenderOptionType = 'MODAL';

  /**
   * filterStyles is an tuple of three number that SHOULD add up to 100
   * - filter - flex-basis
   * - gap - gap between filter and builk action
   * - bulkAction - flex-basis
   *
   * For Example,
   * [filterStyles] = "[60, 10, 30]"
   * // Filter will take 60% width
   * // bulkAction will take 30% of width
   * // with gap of 10% between filter and bulkAction
   */
  @Input() filterStyles: [filter: number, gap: number, bulkAction: number] = [60, 2, 38];
  /**
   * dateType - Date format in selected value for Duration filter
   * - string - Value will be string - "2023-10-16"
   * - number - Value will be number - "1697360062290"
   */
  @Input() dateType: 'string' | 'number' | undefined = 'number';
  @Output() updatedFilter = new EventEmitter<TFilterGroupModel<T>>();

  constructor(private popoverController: PopoverController, private modalController: ModalController) {}

  #emitfilterUpdate() {
    this.updatedFilter.emit(this.latestFilterGoupModel);
  }

  updateByGroupInfo(updatedModel: TFilterGroupModel<T>['groups'][number]) {
    this.latestFilterGoupModel = UiFilterModifiers.filterUpdateByGroup(this.latestFilterGoupModel, updatedModel);
    this.#emitfilterUpdate();
  }

  removeFilter(removeVal: string | number, groupItem: TFilterGroupModel<T>['groups'][number]) {
    this.latestFilterGoupModel = UiFilterModifiers.removeFilterValue(this.latestFilterGoupModel, groupItem, removeVal);
    this.#emitfilterUpdate();
  }

  clearAllFilters() {
    this.latestFilterGoupModel = UiFilterModifiers.removeAllFilterValue(this.latestFilterGoupModel);
    this.#emitfilterUpdate();
  }

  async openOrgHeirarchyPopover(e: Event, orgTree: TOrgHeirachy, filterGroup: TOrgFilter) {
    const popover = await this.popoverController.create({
      component: OrgHeirarchyComponent,
      event: e,
      componentProps: {
        orgTree: orgTree,
        filteredOrgTree: filterGroup.filteredOrgTree,
      },
      cssClass: 'org-heirarchy-popover',
    });

    await popover.present();

    const { data, role } = await popover.onDidDismiss();
    switch (role) {
      case 'ON_APPLY': {
        const updatedInfo: TOrgFilter = {
          ...filterGroup,
          filteredOrgTree: data?.payload,
        };
        this.updateByGroupInfo(updatedInfo);
      }
    }
  }

  async openDurationFilter(filterGroup: TDurationGroupInfo) {
    const datePickerModalComponent = await this.modalController.create({
      component: DurationPickerComponent,
      cssClass: 'duration-picker-modal',
      backdropDismiss: false,
      componentProps: {
        selectedDateFilter: filterGroup.selectedValues[0],
        customLabelText: filterGroup?.customLabelText,
        dateType: this.dateType,
      },
    });

    await datePickerModalComponent.present();

    const dismissEmitValue = await datePickerModalComponent.onDidDismiss();
    if (dismissEmitValue.role) {
      switch (dismissEmitValue.role) {
        case 'ON_SELECTED':
          {
            const selectedValue = dismissEmitValue.data as TDurationGroupInfo['selectedValues'][number];
            const updatedInfo: TDurationGroupInfo = {
              ...filterGroup,
              items:
                selectedValue && filterGroup.items.length <= 1
                  ? [
                      {
                        value: selectedValue,
                        isSelected: true,
                      },
                    ]
                  : filterGroup.items.map((item) => {
                      if (item.value.type === selectedValue.type) {
                        return {
                          ...item,
                          value: selectedValue,
                          isSelected: true,
                        };
                      } else {
                        return {
                          ...item,
                          isSelected: false,
                        };
                      }
                    }),
              selectedValues: [selectedValue],
            };
            console.log('updatedInfo filter', updatedInfo);
            this.updateByGroupInfo(updatedInfo);
          }
          break;
        case 'ON_CLEAR': {
          const updatedInfo: TDurationGroupInfo = {
            ...filterGroup,
            items: filterGroup.items.map((item) => {
              return {
                ...item,
                value: {
                  type: item.value.type,
                },
                isSelected: false,
              };
            }),
            selectedValues: [],
          };
          this.updateByGroupInfo(updatedInfo);
        }
      }
    }
  }

  onTargetDateChange($event: Event, filterGroup: TTargetDateGroupInfo) {
    const value = ($event.target as HTMLInputElement)?.value;
    const updatedInfo = {
      ...filterGroup,
      selectedValues: value ? [moment(value).format('YYYY-MM-DD')] : [],
    };
    this.updateByGroupInfo(updatedInfo);
  }
}
